/**
 * Zod schemas. Frontend runtime validation of API responses.
 * The Pydantic models in backend/app/schemas/ are the mirror.
 */

import { z } from 'zod';

export const RoleSchema = z.enum(['user', 'admin']);

export const UserSchema = z.object({
  username: z.string(),
  name:     z.string(),
  email:    z.string(),
  role:     RoleSchema,
});

export const FundDescriptorSchema = z.object({
  code:        z.string(),
  displayName: z.string(),
});

export const DatesResponseSchema = z.object({
  dates: z.array(z.string()),
  min:   z.string(),
  max:   z.string(),
});

export const PctOfNavSchema = z.object({
  dtd: z.number().nullable(),
  wtd: z.number().nullable(),
  mtd: z.number().nullable(),
  ytd: z.number().nullable(),
});

export const PerfFeeSchema = z.object({
  perfFee:        z.number(),
  shares:         z.number(),
  sharePrice:     z.number(),
  ytdNetOfFee:    z.number(),
  ytdNetOfFeePct: z.number().nullable(),
});

export const WtdContextSchema = z.object({
  previousFriday: z.string().nullable(),
  available:      z.boolean(),
});

export const AttributionSummarySchema = z.object({
  fund:        z.string(),
  date:        z.string(),
  fundNav:     z.number(),
  officialNav: z.number(),
  dtdPl:       z.number(),
  wtdPl:       z.number().nullable(),
  mtdPl:       z.number(),
  ytdPl:       z.number(),
  wtdContext:  WtdContextSchema,
  fundVar:     z.number(),
  varPctNav:   z.number().nullable(),
  perfFee:     PerfFeeSchema.nullable(),
  pctOfNav:    PctOfNavSchema,
  hwm:                    z.number().nullable(),
  sharePrice:             z.number().nullable(),
  ptT:                    z.number().nullable(),
  ltdSharePricePctChange: z.number().nullable(),
  navBh:                  z.number().nullable(),
  ytdNetOfFeeBh:          z.number().nullable(),
  ytdNetOfFeeBhPct:       z.number().nullable(),
  sharePriceBh:           z.number().nullable(),
  ptTBh:                  z.number().nullable(),
});

export const SqlInspectorSchema = z.object({
  sql:       z.string(),
  params:    z.record(z.union([z.string(), z.number(), z.null()])),
  transform: z.string().nullable(),
});

// Login
export const LoginRequestSchema = z.object({
  username: z.string().min(1),
  password: z.string().min(1),
});

export const LoginResponseSchema = z.object({
  user: UserSchema,
});

export const ForgotPasswordRequestSchema = z.object({
  identifier: z.string().min(1),
});

export const ResetPasswordRequestSchema = z.object({
  token:        z.string().min(20),
  new_password: z.string().min(8),
});

// ── Attribution widget schemas ───────────────────────────────────────────────

export const StrategyRowSchema = z.object({
  strategy: z.string(),
  dtd: z.number(),
  mtd: z.number(),
  ytd: z.number(),
});
export const StrategiesResponseSchema = z.object({
  strategies: z.array(StrategyRowSchema),
});

export const PositionRowSchema = z.object({
  name:        z.string(),
  assetClass:  z.string(),
  region:      z.string(),
  strategy:    z.string(),
  secType:     z.string(),
  currency:    z.string(),
  ls:          z.string(),
  position:    z.number(),
  marketValue: z.number(),
  exposurePct: z.number(),
  dtd:         z.number(),
  mtd:         z.number(),
  ytd:         z.number(),
});
export const PositionsResponseSchema = z.object({
  positions: z.array(PositionRowSchema),
});

export const WinnerRowSchema = z.object({
  security: z.string(),
  dtd:      z.number(),
  mtd:      z.number(),
  ytd:      z.number(),
});
export const WinnersResponseSchema = z.object({
  period:  z.string(),
  winners: z.array(WinnerRowSchema),
  losers:  z.array(WinnerRowSchema),
});

export const DrilldownPositionSchema = z.object({
  security:    z.string(),
  position:    z.number(),
  exposurePct: z.number(),
  dtd:         z.number(),
  mtd:         z.number(),
  ytd:         z.number(),
  isClosed:    z.boolean(),
});
export const DrilldownStrategySchema = z.object({
  strategy:  z.string(),
  dtd:       z.number(),
  mtd:       z.number(),
  ytd:       z.number(),
  positions: z.array(DrilldownPositionSchema),
});
export const DrilldownResponseSchema = z.object({
  strategies: z.array(DrilldownStrategySchema),
});

export const ExposureRowSchema = z.object({
  label: z.string(),
  value: z.number(),
});
export type ExposureRow = z.infer<typeof ExposureRowSchema>;
export const ExposureResponseSchema = z.object({
  assetClass: z.array(ExposureRowSchema),
  region:     z.array(ExposureRowSchema),
  industry:   z.array(ExposureRowSchema),
  country:    z.array(ExposureRowSchema),
});

export const TreasuryPositionSchema = z.object({
  security:    z.string(),
  strategy:    z.string(),
  secType:     z.string(),
  currency:    z.string(),
  position:    z.number(),
  marketValue: z.number(),
  exposurePct: z.number(),
  dtd:         z.number(),
  mtd:         z.number(),
  ytd:         z.number(),
});
export type TreasuryPosition = z.infer<typeof TreasuryPositionSchema>;
export const TreasurySummarySchema = z.object({
  totalMarketValue: z.number(),
  pctOfNav:         z.number(),
  mtdPl:            z.number(),
  ytdPl:            z.number(),
});
export const TreasuryResponseSchema = z.object({
  summary: TreasurySummarySchema,
  mmf:     z.array(TreasuryPositionSchema),
  clo:     z.array(TreasuryPositionSchema),
});

export const CommoditySummarySchema = z.object({
  marketValue: z.number(),
  exposurePct: z.number(),
  dtdPl:       z.number(),
  mtdPl:       z.number(),
  ytdPl:       z.number(),
});
export const CommodityChartRowSchema = z.object({
  security:    z.string(),
  exposurePct: z.number(),
  dtd:         z.number(),
  mtd:         z.number(),
  ytd:         z.number(),
});
export const CommodityPositionSchema = z.object({
  security:    z.string(),
  assetClass:  z.string(),
  ls:          z.string(),
  position:    z.number(),
  marketValue: z.number(),
  exposurePct: z.number(),
  dtd:         z.number(),
  mtd:         z.number(),
  ytd:         z.number(),
});
export const CommoditiesResponseSchema = z.object({
  summary:   CommoditySummarySchema,
  chartRows: z.array(CommodityChartRowSchema),
  positions: z.array(CommodityPositionSchema),
});

// Change password
export const ChangePasswordSchema = z.object({
  current_password: z.string().min(1, 'Required'),
  new_password:     z.string().min(8, 'At least 8 characters'),
  confirm_password: z.string(),
}).refine((d) => d.new_password === d.confirm_password, {
  path: ['confirm_password'],
  message: 'Passwords do not match',
});

// ── Dashboards ────────────────────────────────────────────────────────────────

export const WidgetRowSchema = z.object({
  id:         z.number(),
  position:   z.number(),
  title:      z.string(),
  sql:        z.string(),
  x_col:      z.string().nullable(),
  y_col:      z.string().nullable(),
  chart_type: z.string(),
  col_width:  z.number(),
  widget_key: z.string(),
  x:          z.number(),
  y:          z.number(),
  w:          z.number(),
  h:          z.number(),
  options:    z.record(z.unknown()).nullable(),
});
export type WidgetRow = z.infer<typeof WidgetRowSchema>;

export const LayoutItemSchema = z.object({
  id: z.number(),
  x:  z.number(),
  y:  z.number(),
  w:  z.number(),
  h:  z.number(),
});
export type LayoutItem = z.infer<typeof LayoutItemSchema>;

export const LayoutUpdateRequestSchema = z.object({
  items: z.array(LayoutItemSchema),
});

export const DashboardSummarySchema = z.object({
  id:           z.number(),
  name:         z.string(),
  is_published: z.boolean(),
  updated_at:   z.string(),
});
export type DashboardSummary = z.infer<typeof DashboardSummarySchema>;

export const DashboardDetailSchema = DashboardSummarySchema.extend({
  widgets: z.array(WidgetRowSchema),
});
export type DashboardDetail = z.infer<typeof DashboardDetailSchema>;

export const DashboardListResponseSchema = z.object({
  dashboards: z.array(DashboardSummarySchema),
});

export const SqlValidateResponseSchema = z.object({
  valid: z.boolean(),
  error: z.string().nullable(),
});

export const SqlPreviewResponseSchema = z.object({
  columns:   z.array(z.string()),
  rows:      z.array(z.record(z.unknown())),
  row_count: z.number(),
  error:     z.string().optional(),
});

// Global widget options
export const WidgetGlobalOptionsRowSchema = z.object({
  widget_key: z.string(),
  options:    z.record(z.unknown()),
  updated_by: z.string().nullable(),
  updated_at: z.string(),
});
export type WidgetGlobalOptionsRow = z.infer<typeof WidgetGlobalOptionsRowSchema>;

export const WidgetGlobalOptionsListResponseSchema = z.object({
  widgets: z.array(WidgetGlobalOptionsRowSchema),
});

// Attribution Reports published view settings
export const AttributionWidgetInstanceSchema = z.object({
  instanceId: z.string(),
  widgetKey:  z.string(),
  x:          z.number(),
  y:          z.number(),
  w:          z.number(),
  h:          z.number(),
  options:    z.record(z.unknown()).optional(),
});

export const AttributionCustomTabSchema = z.object({
  id:      z.string(),
  name:    z.string(),
  widgets: z.array(AttributionWidgetInstanceSchema),
});

export const AttributionViewStateSchema = z.object({
  tabs: z.array(AttributionCustomTabSchema).default([]),
  tabOrder: z.array(z.string()).default([]),
  defaultTabOverrides: z.record(z.array(AttributionWidgetInstanceSchema)).default({}),
});
export type AttributionViewState = z.infer<typeof AttributionViewStateSchema>;

export const AttributionViewSettingsRowSchema = z.object({
  view_key:   z.string(),
  state:      AttributionViewStateSchema,
  updated_by: z.string().nullable(),
  updated_at: z.string(),
});
export type AttributionViewSettingsRow = z.infer<typeof AttributionViewSettingsRowSchema>;

export const AttributionViewSettingsNullableSchema = AttributionViewSettingsRowSchema.nullable();

// Print preview saved views (server-shared). The backend Pydantic models use
// `X | None = None` for these fields, which FastAPI serializes as an explicit
// `null` (not an omitted key) — so these must be `.nullable()`, not just
// `.optional()` (which only accepts `undefined`, and rejects `null`).
export const ReportWidgetSchema = z.object({
  instanceId: z.string(),
  widgetKey: z.string(),
  options: z.record(z.unknown()).nullable().optional(),
  scale: z.number(),
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
});
export type ReportWidget = z.infer<typeof ReportWidgetSchema>;

export const ReportPageSchema = z.object({
  id: z.string(),
  orientation: z.enum(['landscape', 'portrait']).nullable().optional(),
  showHeader: z.boolean().nullable().optional(),
  widgets: z.array(ReportWidgetSchema).default([]),
});
export type ReportPage = z.infer<typeof ReportPageSchema>;

// includeInEmail / emailFunds (2026-07 "Save As" + multi-template daily
// email): these two live inside the JSONB `definition` blob, not as DB
// columns. Legacy rows saved before this feature simply OMIT the keys
// (there is no explicit `null`), and the backend Pydantic `SavedPrintView`
// model fills concrete defaults (`False` / `[]`) for any missing key when
// it validates the JSONB into a model on read — so the API never actually
// serializes an omitted key or a `null` for these two. Still, `.optional()`
// (not `.nullable()`) is the correct/defensive choice here since the
// underlying JSONB genuinely omits rather than nulls the key.
export const SavedPrintViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  orientation: z.enum(['landscape', 'portrait']),
  pages: z.array(ReportPageSchema).nullable().optional(),
  widgets: z.array(ReportWidgetSchema).nullable().optional(),
  includeInEmail: z.boolean().optional().default(false),
  emailFunds: z.array(z.string()).optional().default([]),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SavedPrintView = z.infer<typeof SavedPrintViewSchema>;

export const PrintViewRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  definition: SavedPrintViewSchema,
  updated_by: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type PrintViewRow = z.infer<typeof PrintViewRowSchema>;

export const PrintViewRowListSchema = z.array(PrintViewRowSchema);
export const PrintViewRowNullableSchema = PrintViewRowSchema.nullable();

// ── Users ─────────────────────────────────────────────────────────────────────

export const UsersListResponseSchema = z.object({
  users: z.array(UserSchema),
});
